import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MediaSelector } from '@/components/cms/exhibit-form/MediaSelector';
import { toast } from 'sonner';

interface HomeImageSettingsProps {
  showHomeImage: boolean;
  homeImageSettings: {
    imageUrl: string;
    altText: string;
    caption: string;
    size: 'small' | 'medium' | 'large';
  };
  saving: boolean;
  onUpdateSetting: (key: string, value: boolean | object) => Promise<void>;
}

export function HomeImageSettings({
  showHomeImage,
  homeImageSettings,
  saving,
  onUpdateSetting
}: HomeImageSettingsProps) {
  const [showImageDialog, setShowImageDialog] = useState(false);

  const updateImageSetting = async (key: string, value: string) => {
    const newSettings = {
      ...homeImageSettings,
      [key]: value
    };
    await onUpdateSetting('homeImageSettings', newSettings);
  };

  const handleImageSelect = async (mediaFile: any) => {
    await updateImageSetting('imageUrl', mediaFile.url);
    setShowImageDialog(false);
    toast.success('Home image updated successfully');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Home Image</CardTitle>
        <CardDescription>
          Display a featured image on the home page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Show Home Image</Label>
            <p className="text-sm text-muted-foreground">
              Display an image section on the home page
            </p>
          </div>
          <Switch
            checked={showHomeImage}
            onCheckedChange={(value) => onUpdateSetting('showHomeImage', value)}
            disabled={saving}
          />
        </div>

        {showHomeImage && (
          <div className="space-y-4 pt-4 border-t">
            <div className="space-y-3">
              <Label>Home Image</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 border border-border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                  <img 
                    src={homeImageSettings.imageUrl} 
                    alt="Home Image Preview" 
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Current Image</p>
                  <p className="text-xs text-muted-foreground">
                    Click "Change Image" to upload a new image or select from media library
                  </p>
                </div>
                <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={saving}>
                      Change Image
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <DialogHeader>
                      <DialogTitle>Select Home Image</DialogTitle>
                    </DialogHeader>
                    <MediaSelector
                      type="image"
                      language=""
                      onSelect={handleImageSelect}
                    />
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                <Label htmlFor="imageUrl">Image URL (Optional)</Label>
                <Input
                  id="imageUrl"
                  placeholder="Or enter image URL manually"
                  value={homeImageSettings.imageUrl}
                  onChange={(e) => updateImageSetting('imageUrl', e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="altText">Alt Text</Label>
              <Input
                id="altText"
                placeholder="Describe the image for accessibility"
                value={homeImageSettings.altText}
                onChange={(e) => updateImageSetting('altText', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caption">Caption (Optional)</Label>
              <Textarea
                id="caption"
                placeholder="Optional caption text to display below the image"
                value={homeImageSettings.caption}
                onChange={(e) => updateImageSetting('caption', e.target.value)}
                disabled={saving}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="size">Image Size</Label>
              <Select
                value={homeImageSettings.size}
                onValueChange={(value) => updateImageSetting('size', value)}
                disabled={saving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select image size" />
                </SelectTrigger>
                 <SelectContent className="bg-white">
                   <SelectItem value="small">Small (max height: 192px)</SelectItem>
                   <SelectItem value="medium">Medium (max height: 256px)</SelectItem>
                   <SelectItem value="large">Large (max height: 384px)</SelectItem>
                 </SelectContent>
              </Select>
            </div>

            {homeImageSettings.imageUrl && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="p-4 bg-muted/50 rounded-lg border">
                  <img
                    src={homeImageSettings.imageUrl}
                    alt={homeImageSettings.altText || 'Preview'}
                    className={`w-full ${
                      homeImageSettings.size === 'small' ? 'max-h-48' :
                      homeImageSettings.size === 'large' ? 'max-h-96' : 'max-h-64'
                    } object-contain rounded-lg`}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  {homeImageSettings.caption && (
                    <p className="text-center text-muted-foreground text-sm mt-2">
                      {homeImageSettings.caption}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}